/**
 * Sanitize template data for duplicated/forked templates.
 *
 * Duplicated templates must start disconnected from all integration state
 * (SnapLink, SnapPage, API Builder, public endpoints). This utility strips
 * those keys from the `data` JSON column before insert so the new template
 * behaves as a fresh draft.
 */

/** Keys that must be removed or reset in template.data on duplication */
const INTEGRATION_KEYS_TO_STRIP: readonly string[] = [
  // SnapLink
  'activated_at',
  'slug',
  // SnapPage
  'snap_page_id',
  'snap_page_enabled',
  'snap_page_slug',
  'snappage_id',
  'snappage_enabled',
  // API Builder
  'api_builder_id',
  'api_builder_enabled',
  'api_builder_api_key',
  'api_builder_config_id',
  // Public endpoint
  'public_endpoint',
  'public_endpoint_enabled',
  'public_endpoint_url',
];

/**
 * Returns a shallow copy of `data` with integration/activation metadata
 * stripped and status forced to 'draft'.
 *
 * Safe to call with null/undefined — returns an empty object.
 */
export function sanitizeDuplicateData(
  data: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  if (!data || typeof data !== 'object') return {};

  const cleaned: Record<string, unknown> = { ...data };

  // Force status to draft
  cleaned.status = 'draft';

  // Strip integration keys
  for (const key of INTEGRATION_KEYS_TO_STRIP) {
    delete cleaned[key];
  }

  return cleaned;
}
